import type { IconName } from "@mdotm/shadcn/legacy/components";
import { unreachable } from "@mdotm/utils/runtime";
import { takesRelativeness } from "./representation-mode";
import type { PersistedConceptFields, WorkspaceChartTypeEnum } from "./spec-gaps";
import type {
	ConceptDefinitionDTO,
	ParameterizedConceptDTO,
	SmartWorkspaceDTOChart,
} from "@eu.mdotm.api/smart-workspaces";

/**
 * Canonical display order, so pickers and derived lists are always ordered the same way.
 *
 * `DENSITY` sits next to `FREQUENCY` because they are two readings of one aggregation — the same
 * request, the same bins, counted on one and turned into a density on the other — and the picker should
 * offer them as the pair they are rather than with the line chart between them.
 */
export const workspaceChartTypes: ReadonlyArray<WorkspaceChartTypeEnum> = [
	"HISTOGRAM",
	"STACKED_COLUMN",
	"FREQUENCY",
	"DENSITY",
	"PERFORMANCE_LINE",
];

/**
 * `true` when the persisted value is one this build can draw — the enum is the backend's to extend, so a
 * workspace can name a chart type no switch here answers.
 *
 * One caller, by design: `knownCharts` in `./ctx/adapter`, which drops such a chart as the workspace is
 * read. Everything downstream branches on the type freely, and the switches stay exhaustive so a *new*
 * member is still a compile error. Do not add a second call — a consumer that feels it needs one is
 * reading `workspace.charts` around the adapter.
 */
export function isKnownChartType(chartType: string | undefined): chartType is WorkspaceChartTypeEnum {
	return chartType != null && (workspaceChartTypes as ReadonlyArray<string>).includes(chartType);
}

/**
 * What the user sees for each wire value, which is not what the wire calls it: `HISTOGRAM` draws a column
 * chart and `PERFORMANCE_LINE` draws a line — see the doc on {@link WorkspaceChartTypeEnum}. These labels
 * are the only place that renaming happens; the code branches on the wire value everywhere.
 */
export const workspaceChartTypeLabels: Record<WorkspaceChartTypeEnum, string> = {
	HISTOGRAM: "Column",
	STACKED_COLUMN: "Stacked column",
	FREQUENCY: "Frequency",
	DENSITY: "Density",
	PERFORMANCE_LINE: "Line",
};

/**
 * One glyph per chart type, from the `chart-type-*` set design drew for this picker.
 *
 * `chart-type-cols-4-line` draws two lines and no columns — the name is the designer's and does not
 * describe it.
 */
export const workspaceChartTypeIcons: Record<WorkspaceChartTypeEnum, IconName> = {
	HISTOGRAM: "chart-type-cols-ver",
	STACKED_COLUMN: "chart-type-stacked",
	FREQUENCY: "chart-type-cols-4-frequency",
	DENSITY: "chart-type-bell",
	PERFORMANCE_LINE: "chart-type-cols-4-line",
};

/**
 * Which chart types can draw what the chain currently describes (FR-1.2: incompatible chart types are
 * not offered).
 *
 * The deciding fact is the *shape of the value*, not the metric: a chain that ends in a breakdown
 * yields one number per category, which only the stacked column can draw — and conversely a stacked
 * column has nothing to stack without one. So the two are not independent choices, and asking for the
 * breakdown separately would only create states where they disagree.
 *
 * - a concept that publishes a `timeSeries` form ⟹ line, *in addition to* whatever its scalar value can
 *   draw. The line is a second reading of the same metric, not a different metric: it is requested by
 *   flagging the concept (`timeSeries: true`), so nothing about the scalar stops being true;
 * - a breakdown in the chain ⟹ stacked column, and nothing else;
 * - otherwise the value is a scalar: numeric metrics get the column chart, the frequency chart and the
 *   density one, non-numeric ones chart as nothing.
 *
 * The frequency and the density charts are offered under exactly the same condition, and that is not a
 * coincidence to be factored out: they are one aggregation read two ways (counts per bin vs. a
 * probability density over the same bins), so anything that makes one drawable makes the other drawable.
 *
 * Derived frontend-side for V1. The backend offered to publish a per-concept compatibility list on
 * `ConceptDefinitionDTO` instead; keeping the decision in this one function makes swapping to it a
 * one-function change.
 */
export function supportedChartTypes(
	concept: ConceptDefinitionDTO | undefined,
	chain?: { breakdown?: { dimension: string } },
): Array<WorkspaceChartTypeEnum> {
	if (!concept) {
		return [];
	}

	const isNumeric = concept.dataType === "NUMERIC" || concept.dataType === "CUSTOM_ATTRIBUTE_NUMBER";

	return workspaceChartTypes.filter((chartType) => {
		switch (chartType) {
			case "HISTOGRAM":
			case "FREQUENCY":
			case "DENSITY":
				return isNumeric && chain?.breakdown == null;
			case "STACKED_COLUMN":
				return chain?.breakdown != null;
			case "PERFORMANCE_LINE":
				// A breakdown and a history are both "instead of the single value", so they cannot be
				// asked for together — and a concept that has a history form declares no breakdown
				// dimensions anyway, which makes this guard belt-and-braces rather than load-bearing.
				return conceptSupportsTimeSeries(concept) && chain?.breakdown == null;
			default:
				return unreachable(chartType);
		}
	});
}

/**
 * `true` when the concept can also be resolved as a history, i.e. when the line chart can draw it.
 *
 * The single place that reads the capability. It is a *form* of the concept rather than a concept of its
 * own: the backend keeps the definition that resolves it (`systemId: performanceLine`) out of the
 * catalogue and publishes its parameters nested under the parent instead, so what used to be a second
 * "Performance — line" leaf beside "Performance" in every picker is now one leaf that can be drawn as a
 * line. Ask for it with `timeSeries: true` on the parameterized concept, and parameterize it from
 * {@link timeSeriesFormOf} rather than from the concept's own fields.
 */
export function conceptSupportsTimeSeries(concept: ConceptDefinitionDTO | undefined): boolean {
	return concept?.timeSeries != null;
}

/**
 * The parameters to build the chain from, for a given chart type.
 *
 * `PERFORMANCE_LINE` reads the nested `timeSeries` form; every other chart type reads the concept's own fields. They are
 * genuinely different answers, not a defensive copy — realised performance is CURRENT-only and
 * relativisable as a single number, while its history also covers PROPOSAL, reaches back 2Y/3Y and is
 * ABSOLUTE-only (there is no `syntheticMetrics.performance2Y` to read, and the series is not
 * relativised). Reading the wrong one offers combinations the backend cannot answer.
 *
 * Falls back to the concept's own fields when a `PERFORMANCE_LINE` chart's concept publishes no form, which a
 * consistent catalogue never produces — `supportedChartTypes` would not have offered `PERFORMANCE_LINE` — but a
 * chart persisted before the concept lost its history form would.
 */
export function timeSeriesFormOf(
	concept: ConceptDefinitionDTO | undefined,
	chartType: WorkspaceChartTypeEnum,
): ConceptParameterForm {
	return (chartType === "PERFORMANCE_LINE" ? concept?.timeSeries : undefined) ?? concept ?? {};
}

/**
 * Which values a concept accepts for each of its parameters — the lists the chain turns into selects.
 *
 * A concept has two of these and they do not agree: its own fields, and the nested `timeSeries` form its
 * history accepts. {@link timeSeriesFormOf} is what picks between them, and nothing downstream should have
 * to know that there were two.
 */
export type ConceptParameterForm = Pick<
	ConceptDefinitionDTO,
	"allowedSource" | "allowedTimeHorizons" | "allowedConfidenceLevels" | "representationModes" | "relativenessModes"
>;

/** The parameters a {@link ConceptParameterForm} constrains: one field per list it publishes. */
export type ClampableConceptParameters = Pick<
	ParameterizedConceptDTO,
	"timeHorizon" | "confidenceLevel" | "representationMode" | "source"
>;

/**
 * The parameters, made legal for the form — "which values may this concept take, in this form", answered
 * once.
 *
 * A value that was legal under one form can be illegal under another, and switching forms is something the
 * user does routinely: 3Y is a horizon the performance *history* has and its scalar does not, so a line
 * chart edited into a column would go on requesting a synthetic metric that does not exist. Relativeness is
 * the same story pointing the other way — performance relativises as a single number and its series does
 * not, so carrying `RELATIVE` onto a line asks for something the history form never declared, and since the
 * engine enforces only the source the answer comes back absolute and is presented as relative.
 *
 * **Falls back to the form's first allowed value rather than dropping the parameter.** A metric with no
 * horizon is a request the backend cannot answer at all, which is worse than one at the wrong horizon. With
 * no incoming value that same rule *is* the seed rule — `clampToForm({}, form)` picks the first of each —
 * which is why the chain's chart-type-change effect and its concept-picked seed path are one function rather
 * than two that happen to agree.
 *
 * `source` is the one field with a floor: every concept resolves against some source, so a form that
 * declares none still yields `CURRENT`.
 *
 * `relativeness` is deliberately not clamped here. It is not one of the form's lists — it names an entity,
 * and what makes it legal is the *mode* it qualifies rather than anything the concept published. Dropping it
 * alongside a `RELATIVE` that did not survive is therefore the write direction's rule, and it lives in
 * {@link chartSeriesConcepts}.
 */
export function clampToForm(
	params: ClampableConceptParameters,
	form: ConceptParameterForm,
): ClampableConceptParameters {
	const stillAllowed = <T>(value: T | undefined, allowed: Array<T> | undefined): T | undefined =>
		value != null && allowed?.includes(value) ? value : allowed?.at(0);

	return {
		timeHorizon: stillAllowed(params.timeHorizon, form.allowedTimeHorizons),
		confidenceLevel: stillAllowed(params.confidenceLevel, form.allowedConfidenceLevels),
		representationMode: stillAllowed(params.representationMode, form.representationModes),
		source: stillAllowed(params.source, form.allowedSource) ?? "CURRENT",
	} satisfies Record<keyof ClampableConceptParameters, unknown>;
}

/**
 * The series a chart draws for a concept: one per source the *chosen chart type's form* allows, carrying
 * parameters clamped to that same form.
 *
 * Both halves matter, and both are about the line chart, whose form is not the concept's own. Reading
 * `concept.allowedSource` for a line draws the CURRENT series and silently drops the PROPOSAL one — which
 * is the entire comparison the chart exists for, since realised performance declares CURRENT alone while
 * its history covers both. And a parameter that was legal for the scalar can be illegal for the history:
 * performance relativises as a single number and its series does not, so carrying `RELATIVE` over asks
 * for something the history form does not declare — the engine enforces only the source, so the answer
 * comes back absolute and is presented as relative.
 *
 * Clamping is {@link clampToForm} — the same function the chain's chart-type-change effect and its seed
 * path call, rather than a copy of the rule that has to be kept in step with them. This one used to be that
 * copy: it was written while fixing the bug the *missing* clamp caused, and the rule was duplicated instead
 * of shared, which is how the family grew from two sites to three. The panel gets clamping from the chain
 * as the user edits; a caller that builds a chart without a panel — the table's "Create Chart" quick
 * action — has to ask for it here.
 *
 * The clamped `source` is the one answer not used: sources are *enumerated* here rather than picked, because
 * one series per allowed source is the whole point.
 */
export function chartSeriesConcepts(
	concept: ConceptDefinitionDTO,
	chartType: WorkspaceChartTypeEnum,
	from: Pick<
		ParameterizedConceptDTO,
		"label" | "timeHorizon" | "confidenceLevel" | "representationMode" | "relativeness" | "parameters"
	>,
): ParameterizedConceptDTO[] {
	const form = timeSeriesFormOf(concept, chartType);
	const { representationMode, timeHorizon, confidenceLevel } = clampToForm(from, form);

	return (form.allowedSource ?? []).map(
		(source): ParameterizedConceptDTO =>
			({
				conceptId: concept.id!,
				label: from.label,
				source,
				timeHorizon,
				confidenceLevel,
				representationMode,
				// A relativeness only means anything next to a mode that takes one, so it follows the mode it
				// qualifies instead of outliving it.
				relativeness: takesRelativeness(representationMode) ? from.relativeness : undefined,
				parameters: from.parameters,
			}) satisfies PersistedConceptFields,
	);
}

/**
 * The request-time form of a chart's series concepts: two chart types ask the backend to resolve their metric
 * as something other than one number per portfolio, and this is where they say so.
 *
 * Both flags stay off the *persisted* concept. That pool is what "Create Column" and "Create Filter"
 * copy a chart's concept out of, and what the table's own request draws from, so a persisted concept has
 * to keep meaning "one number" — the chart's `chartType` already records which reading the chart wants,
 * making the flag derivable rather than something two submit handlers must remember to set.
 *
 * - `STACKED_COLUMN` carries `breakdown`, whose dimension is configured per chart (`breakdownProperties`);
 * - `PERFORMANCE_LINE` carries `timeSeries`, which has nothing to configure: wanting the line *is* the request. The
 *   parameters its history accepts are a separate question, answered by {@link timeSeriesFormOf} when the
 *   series are built.
 *
 * Only the chart's own `series` are flagged: the concepts a request carries also include whatever the
 * search predicate and the sort name, and those still want their single value.
 */
export function withChartSeriesRequest(
	concepts: ReadonlyArray<ParameterizedConceptDTO>,
	chart: Pick<SmartWorkspaceDTOChart, "chartType" | "series" | "breakdownProperties">,
): ParameterizedConceptDTO[] {
	const request = chartSeriesRequest(chart);
	if (request === undefined) {
		return [...concepts];
	}

	return concepts.map((concept) =>
		concept.uuid && chart.series.includes(concept.uuid) ? { ...concept, ...request } : concept,
	);
}

/**
 * What {@link withChartSeriesRequest} applies, or `undefined` when the chart type reads the concept's own value
 * — including a stacked column with no dimension picked yet, which must not gain an empty `breakdown`
 * key it would then have to be compared against.
 */
function chartSeriesRequest(
	chart: Pick<SmartWorkspaceDTOChart, "chartType" | "breakdownProperties">,
): Pick<ParameterizedConceptDTO, "breakdown" | "timeSeries"> | undefined {
	const chartType = chart.chartType;
	switch (chartType) {
		case "STACKED_COLUMN":
			return chart.breakdownProperties ? { breakdown: chart.breakdownProperties } : undefined;
		case "PERFORMANCE_LINE":
			return { timeSeries: true };
		case "HISTOGRAM":
		case "FREQUENCY":
		case "DENSITY":
			return undefined;
		default:
			return unreachable(chartType);
	}
}

/**
 * `true` when the concept can be broken down at all, i.e. when the chain will offer an "All" leaf
 * somewhere. Only useful to decide whether to *ask*; whether a breakdown was actually picked is
 * `chain.breakdown`.
 */
export function conceptSupportsBreakdown(concept: ConceptDefinitionDTO | undefined): boolean {
	return (concept?.breakdownDimensions?.length ?? 0) > 0;
}

/**
 * How ordering works for a chart type — the two chart types that draw portfolios on the X axis do not order
 * them the same way.
 *
 * - `WORKSPACE`: one number per portfolio, so the backend's comparator can order them. That is the single
 *   global workspace sort, shared with the table.
 * - `CATEGORY`: portfolios on the X axis, but the charted value is a breakdown — a number *per category*,
 *   which no comparator can order by itself. What the user picks is which stacked segment (or the whole
 *   stack) decides the order, and the frontend applies it to the rows it fetched: `SortCriterionDTO`
 *   carries only a parameterized-concept uuid with no way to name a category, and a breakdown concept
 *   declares zero comparison operators and `sortable = false`.
 * - `NONE`: the X axis is not portfolios — bins for the frequency and the density charts, time for the
 *   line — so there is nothing to order.
 */
export type WorkspaceChartSortingMode = "WORKSPACE" | "CATEGORY" | "NONE";

/**
 * `true` when the chart type has an axis that carries metric *values*, i.e. somewhere to put the
 * key-metric threshold line (FR-8).
 *
 * The threshold marks one number on the charted metric's own scale, so the question is only whether the
 * chart draws that scale:
 *
 * - `HISTOGRAM` draws it on Y — the original home of the threshold, and the only chart type whose line is
 *   draggable;
 * - `FREQUENCY` and `DENSITY` draw it on X, so their line is vertical rather than horizontal. Same
 *   setting, same three modes (Manual / Portfolio / Reference), different axis;
 * - `STACKED_COLUMN` has no such axis: its Y is a *sum over categories*, and a threshold drawn across a
 *   stack would compare a single-metric value against a total that is not the same quantity;
 * - `PERFORMANCE_LINE` has time on X and one series per portfolio on Y — the same objection the spec makes about
 *   sorting and band selection applies here.
 *
 * The single place that says so, so the two submit handlers, the settings panel and the chart items
 * cannot disagree about which chart types persist a threshold.
 */
export function chartTypeSupportsThreshold(chartType: WorkspaceChartTypeEnum): boolean {
	switch (chartType) {
		case "HISTOGRAM":
		case "FREQUENCY":
		case "DENSITY":
			return true;
		case "STACKED_COLUMN":
		case "PERFORMANCE_LINE":
			return false;
		default:
			return unreachable(chartType);
	}
}

/**
 * Whether the chart type draws a legend at all.
 *
 * The rule is what the legend would *name*:
 *
 * - `STACKED_COLUMN` names the breakdown categories and `PERFORMANCE_LINE` names the portfolios — a colour-to-thing
 *   mapping the reader cannot recover from the plot alone, so the legend earns its space (and its
 *   Show-legend checkbox);
 * - `DENSITY` names its two series *and* the dashed fitted-normal stroke, which no toggle in the header
 *   says. Its legend is a static key of its own — `DensityChartLegend`, drawn in React — rather than
 *   Highcharts' clickable one, but it is persisted and toggled like any other;
 * - `HISTOGRAM` and `FREQUENCY` have only CURRENT and PROPOSAL to name, and the platform says that with
 *   the "Highlight proposal" toggle instead. A Current/Proposal legend restates the toggle in a second
 *   vocabulary, so these two draw none.
 *
 * The single place that says so, so the charts, the settings panel and the chart-creation menu cannot
 * disagree about which chart types persist a legend.
 */
export function chartTypeDrawsLegend(chartType: WorkspaceChartTypeEnum): boolean {
	switch (chartType) {
		case "STACKED_COLUMN":
		case "PERFORMANCE_LINE":
		case "DENSITY":
			return true;
		case "HISTOGRAM":
		case "FREQUENCY":
			return false;
		default:
			return unreachable(chartType);
	}
}

export function chartTypeSortingMode(chartType: WorkspaceChartTypeEnum): WorkspaceChartSortingMode {
	switch (chartType) {
		case "HISTOGRAM":
			return "WORKSPACE";
		case "STACKED_COLUMN":
			return "CATEGORY";
		case "FREQUENCY":
		case "DENSITY":
		case "PERFORMANCE_LINE":
			return "NONE";
		default:
			return unreachable(chartType);
	}
}
